import React, { useEffect, useRef, useState } from "react";
import ReactHtmlParser from "react-html-parser";

const DESCRIPTION_SENTENCE_LIMIT = 5;
const SENTENCE_PATTERN = /[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g;

const normalizeText = (text = "") => text.replace(/\s+/g, " ").trim();

const stripHtml = (html = "") => {
  if (typeof document !== "undefined") {
    const element = document.createElement("div");
    element.innerHTML = html;
    return (element.textContent || element.innerText || "").trim();
  }

  return html.replace(/<[^>]*>/g, "").trim();
};

const getTextExcerpt = (text = "") => {
  const sentences = normalizeText(text).match(SENTENCE_PATTERN) || [];

  return sentences.slice(0, DESCRIPTION_SENTENCE_LIMIT).join(" ").trim();
};

const getDescriptionExcerptHtml = (html = "") => {
  if (!html) {
    return "";
  }

  if (typeof document === "undefined") {
    return getTextExcerpt(stripHtml(html));
  }

  const template = document.createElement("template");
  template.innerHTML = html;
  let remainingSentences = DESCRIPTION_SENTENCE_LIMIT;
  let truncated = false;

  const truncateNode = (node: Node) => {
    if (truncated) {
      node.parentNode?.removeChild(node);
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const sentences = node.textContent?.match(SENTENCE_PATTERN) || [];

      if (sentences.length <= remainingSentences) {
        remainingSentences -= sentences.length;
        return;
      }

      node.textContent = sentences
        .slice(0, remainingSentences)
        .join("")
        .replace(/\s+$/, "");
      remainingSentences = 0;
      truncated = true;
      return;
    }

    Array.from(node.childNodes).forEach(truncateNode);
  };

  truncateNode(template.content);

  return template.innerHTML.trim();
};

const Description = ({ course }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const description = course.formatted_description || "";
  const descriptionText = normalizeText(stripHtml(description));
  const descriptionExcerpt = getDescriptionExcerptHtml(description);
  const descriptionExcerptText = normalizeText(stripHtml(descriptionExcerpt));
  const shouldShowReadMore =
    Boolean(description) && descriptionText !== descriptionExcerptText;

  useEffect(() => {
    if (!modalOpen) {
      document.body.classList.remove("modal-open");
      return;
    }

    document.body.classList.add("modal-open");
    const closeButton = closeButtonRef.current;

    if (closeButton) {
      setTimeout(() => closeButton.focus(), 30);
    }

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setModalOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.classList.remove("modal-open");
    };
  }, [modalOpen]);

  const trapModalFocus = (event) => {
    if (event.key !== "Tab" || !modalRef.current) {
      return;
    }

    const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
      'button, a[href], [tabindex]:not([tabindex="-1"])',
    );

    if (!focusableElements.length) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  return (
    <>
      <section className="fit-section" aria-labelledby="fit-title">
        <h2 className="sr-only" id="fit-title">
          Course fit
        </h2>
        <div className="container">
          <div className="fit-grid">
            <div className="fit-card">
              <h3 className="fit-heading">About this course</h3>
              {descriptionExcerpt && (
                <div className="fit-excerpt">
                  {ReactHtmlParser(descriptionExcerpt)}
                </div>
              )}
              {description && (
                <div id="aboutFullContent" hidden>
                  {ReactHtmlParser(description)}
                </div>
              )}
              {shouldShowReadMore && (
                <div className="fit-readmore-trigger">
                  <button type="button" onClick={() => setModalOpen(true)}>
                    Read more
                  </button>
                </div>
              )}
              <h3 className="fit-heading fit-heading-later">What you'll learn</h3>
              <ul>
                {course.course_skills &&
                  course.course_skills
                    .slice(0, 5)
                    .map((skill, idx) => <li key={idx}>{skill.name}</li>)}
              </ul>
            </div>
            <div className="fit-card gold">
              <div className="fit-illustration">
                <img
                  src={require("../../../../../assets/images/courses/rory-cert.png")}
                  alt="The course on a laptop and phone beside the framed CPD-certified Mini MBA certificate."
                  loading="lazy"
                  decoding="async"
                  width="640"
                  height="360"
                />
              </div>
              <h3 className="fit-heading">Who it's for</h3>
              <ul>
                {course.course_targets &&
                  course.course_targets
                    .slice(0, 5)
                    .map((target, idx) => <li key={idx}>{target.name}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <div
        className="content-modal"
        id="contentModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contentModalTitle"
        aria-hidden={!modalOpen}
        onKeyDown={trapModalFocus}
        ref={modalRef}
      >
        <button
          type="button"
          className="content-modal-backdrop"
          aria-label="Close"
          onClick={() => setModalOpen(false)}
        />
        <div className="content-modal-panel" role="document">
          <button
            type="button"
            className="content-modal-close"
            aria-label="Close"
            onClick={() => setModalOpen(false)}
            ref={closeButtonRef}
          >
            {"\u00d7"}
          </button>
          <header className="content-modal-header">
            <p className="content-modal-eyebrow" id="contentModalEyebrow">
              The behavioural toolkit
            </p>
            <h2 className="content-modal-title" id="contentModalTitle">
              About this course
            </h2>
          </header>
          <div className="content-modal-body">
            {modalOpen && ReactHtmlParser(description)}
          </div>
        </div>
      </div>
    </>
  );
};

export default Description;
